/**
 * MIF import API client (TRUS-149 / TRUS-366).
 *
 * A monthly health-plan Member Information File load is not a generic CSV
 * import: it carries per-plan write policies, a gaps queue, and rollback. It
 * therefore has its own namespace, /api/mif, rather than sharing the workbench's.
 *
 * Everything goes through the shared `apiClient`, which is the only correct way
 * to reach this backend. It supplies three things that are easy to get wrong by
 * hand, and that this module originally DID get wrong:
 *
 *   - the token, read from `auth_token` (or `member_token` for portal users).
 *     An earlier version of this file read `localStorage.getItem('token')`, a key
 *     nothing in this app writes, so every request went out as a bare `Bearer `
 *     and the backend answered "Invalid authorization header format".
 *   - `X-CBO-ID` from `active_cbo_id`. Every MIF endpoint resolves the caller's
 *     organization from it, so without the header a load could read or write
 *     against the wrong org — worse than failing outright.
 *   - the product-aware base URL, plus the deployment-retry interceptor.
 *
 * So: no token parameters, no hand-built headers, no direct axios import.
 */

import { apiClient } from '@/services/apiClient';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * What the loader is allowed to do with a column.
 *
 * `new_only` is the one that matters: the plan echoes ~20 of its 115 columns back
 * from the organization's own prior report, so on an existing member the file's
 * copy is stale by construction.
 */
export type WritePolicy =
  | 'plan_authoritative'
  | 'new_only'
  | 'fill_if_empty'
  | 'ignore'
  | 'unmapped';

export type RowClassification = 'new' | 'matched' | 'ambiguous' | 'unmatched' | 'duplicate';

export type CellAction =
  | 'write'
  | 'skip_org_owned'
  | 'skip_unchanged'
  | 'skip_frozen'
  | 'skip_human_edited'
  | 'skip_empty_source'
  | 'skip_fill_occupied'
  | 'skip_ignored'
  | 'skip_unmapped'
  | 'error';

export type BatchStatus =
  | 'uploaded'
  | 'verified'
  | 'planned'
  | 'committed'
  | 'rolled_back'
  | 'failed'
  | 'abandoned';

export interface MifPack {
  id: number;
  plan_key: string;
  plan_name: string;
  layout_version: string;
  status: 'draft' | 'approved' | 'retired';
  source_note?: string | null;
  proposed_by_agent?: string | null;
  created_at: string;
  approved_at?: string | null;
  approved_by_name?: string | null;
  rule_count: number;
  unmapped_count: number;
  protected_count: number;
  /**
   * Loads actually committed under these rules. The UI must read this rather than
   * assume: the previous copy told admins "loads have run under them" on a pack
   * with zero loads, because it hardcoded the reason instead of checking.
   */
  committed_loads?: number;
  change_count?: number;
}

export interface RuleChange {
  id: number;
  mif_column: string | null;
  change_type:
    | 'pack_installed'
    | 'pack_approved'
    | 'pack_reopened'
    | 'pack_retired'
    | 'column_added'
    | 'column_removed'
    | 'target_changed'
    | 'policy_changed'
    | 'owner_changed'
    | 'transform_changed'
    | 'rationale_changed';
  attribute: string | null;
  old_value: string | null;
  new_value: string | null;
  source: 'human' | 'install' | 'agent';
  status: 'applied' | 'proposed' | 'accepted' | 'rejected';
  note: string | null;
  created_at: string;
  decided_at: string | null;
  actor_name: string | null;
  actor_agent: string | null;
  decided_by_name: string | null;
}

export interface LayoutDrift {
  id: number;
  plan_key: string;
  expected_layout: string | null;
  drift_type: 'column_added' | 'column_missing' | 'column_reordered';
  mif_column: string;
  severity: 'blocking' | 'review' | 'info';
  status: 'open' | 'acknowledged' | 'resolved' | 'dismissed';
  times_seen: number;
  first_seen_at: string;
  last_seen_at: string;
  last_seen_period: string | null;
  resolution_note: string | null;
  resolved_by_name: string | null;
}

export interface DriftSummary {
  open_total: number;
  open_blocking: number;
  acknowledged: number;
  resolved: number;
  last_seen: string | null;
  open_agent_proposals: number;
}

export interface MifFieldRule {
  mif_column: string;
  ordinal: number;
  target_source: 'core' | 'custom' | null;
  target_field: string | null;
  data_owner: 'plan' | 'org' | null;
  write_policy: WritePolicy;
  transform: string;
  rationale?: string | null;
  friendly_name?: string | null;
  field_type?: string | null;
}

export interface ContractDiff {
  // blocking: a required column is missing (identifier, period, production date,
  // plan-owned status) and the file can't be loaded. acknowledge: other mapped
  // columns are missing; their fields are left as they are once someone agrees.
  severity: 'clean' | 'review' | 'acknowledge' | 'blocking';
  expected_count: number;
  actual_count: number;
  missing: string[];
  consequential_missing: string[];
  required_missing?: string[];
  optional_missing?: string[];
  acknowledged_at?: string;
  added: string[];
  reordered: boolean;
  matched_count: number;
  summary: string;
}

export interface PlanSummary {
  rows_total: number;
  rows_to_create: number;
  rows_to_update: number;
  rows_no_change: number;
  rows_needing_review: number;
  classification: Record<string, number>;
  cells_to_write: number;
  cells_skipped: number;
  cell_errors: number;
  action_counts: Record<string, number>;
  /** CBO-entered values this load would have overwritten had it not been stopped. */
  cells_protected: number;
  protected_fields: [string, number][];
  columns_in_file: number;
  columns_unmapped: number;
  columns_ignored: number;
  columns_protected: number;
  churn_candidates: number;
  identity_coverage: {
    members_total: number;
    members_with_cin: number;
    members_without_cin: number;
  };
}

export interface CellDecision {
  mif_column: string;
  field: string | null;
  target_source: 'core' | 'custom' | null;
  label: string | null;
  policy: WritePolicy;
  data_owner: 'plan' | 'org' | null;
  old: string | null;
  new: string | null;
  action: CellAction;
  reason: string | null;
  edited_by?: string;
}

export interface MifRow {
  row_number: number;
  mif_cin: string | null;
  mif_sfhp_id: string | null;
  classification: RowClassification;
  match_method: string | null;
  member_id: number | null;
  member_name: string | null;
  file_first_name: string | null;
  file_last_name: string | null;
  candidates: { member_id: number; name?: string; dob?: string; cin?: string }[] | null;
  resolution: string | null;
  resolution_note: string | null;
  action_taken: string | null;
  writes: CellDecision[];
  protected: CellDecision[];
  cell_errors: CellDecision[];
  decision_count: number;
}

export interface MifGap {
  row_number: number;
  mif_cin: string | null;
  mif_sfhp_id: string | null;
  classification: RowClassification;
  match_method: string | null;
  candidates: { member_id: number; name?: string; dob?: string; cin?: string }[] | null;
  member_id: number | null;
  resolution: string | null;
  resolution_note: string | null;
  errors: { mif_column: string; field: string; error: string }[] | null;
  file_first_name: string | null;
  file_last_name: string | null;
  file_dob: string | null;
}

export interface MifBatch {
  id: number;
  batch_key: string;
  source_filename: string;
  reporting_period: string | null;
  file_prod_date: string | null;
  mcp_identifier: string | null;
  row_count: number | null;
  column_count: number | null;
  status: BatchStatus;
  members_created: number;
  members_updated: number;
  cells_written: number;
  cells_skipped: number;
  rows_unresolved: number;
  rows_churned: number;
  error_message: string | null;
  created_at: string;
  committed_at: string | null;
  rolled_back_at: string | null;
  rollback_note: string | null;
  plan_summary: PlanSummary | null;
  plan_name: string | null;
  plan_key: string | null;
  layout_version: string | null;
  created_by_name: string | null;
  committed_by_name: string | null;
  rolled_back_by_name: string | null;
}

export interface UploadResult {
  batch_id: number;
  plan: { id: number; plan_name: string; plan_key: string; layout_version: string };
  file: { name: string; size_bytes: number; sha256: string; row_count: number; column_count: number };
  metadata: { reporting_period: string | null; file_prod_date: string | null; mcp_identifier: string | null };
  contract: ContractDiff;
  already_loaded_identical_file: { batch_id: number } | null;
  next_step: 'plan' | 'resolve_contract';
}

export interface ChurnItem {
  id: number;
  member_id: number;
  member_cin: string | null;
  member_name: string | null;
  reason: string | null;
  outcome: string | null;
  reviewed_at: string | null;
  reviewed_by_name: string | null;
}

export interface RollbackPreview {
  batch_id: number;
  reporting_period: string | null;
  committed_at: string | null;
  cells_to_revert: number;
  members_affected: number;
  members_to_delete: number;
  conflicts: {
    member_id: number;
    member_name: string;
    field_name: string;
    new_value: string;
    current_value: string;
    last_updated_by: string;
    last_updated_ts: string;
  }[];
  message: string;
}

// ---------------------------------------------------------------------------
// Rule packs
// ---------------------------------------------------------------------------

export interface MifLayout {
  id: number;
  /** null = ships with the platform and installable by any org. Set = this org's own. */
  cbo_id: number | null;
  plan_key: string;
  plan_name: string;
  layout_version: string;
  /**
   * shipped  — seeded from code, hand-verified against real files
   * derived  — inferred from an uploaded file, every policy a default
   * authored — created or materially edited by a person
   *
   * Surfaced prominently: a hand-verified layout and one a machine guessed must
   * never look the same to whoever approves a pack from it.
   */
  origin: 'shipped' | 'derived' | 'authored';
  status: 'draft' | 'published' | 'retired';
  source_note: string | null;
  column_count: number;
  protected_count: number;
  packs_here: number;
  created_at: string;
  published_at: string | null;
  created_by_name: string | null;
  /** States the plan covers, from its payer row (plan_key = lower(payers.payer_id)). */
  states: string[];
  /** This org's own layout, or one it has installed a pack from (TRUS-727). */
  is_mine: boolean;
}

export interface LayoutFilters {
  plan?: string;
  /** Two-letter state; keeps plans that cover it. */
  state?: string;
}

export const listLayouts = async (filters?: string | LayoutFilters) => {
  const f: LayoutFilters = typeof filters === 'string' ? { plan: filters } : filters || {};
  const params: Record<string, string> = {};
  if (f.plan) params.plan = f.plan;
  if (f.state) params.state = f.state;
  return (await apiClient.get(`/api/mif/layouts`, { params })).data as {
    layouts: MifLayout[];
    shipped_modules: { plan_key: string; plan_name: string; layout_version: string; column_count: number }[];
  };
};

export interface DerivedColumn {
  mif_column: string;
  ordinal: number;
  candidate_targets: string[];
  data_owner: 'plan' | 'org' | null;
  write_policy: WritePolicy;
  transform: string;
  rationale: string | null;
  inference_confidence: number | null;
  inference_basis: string | null;
}

export const getLayout = async (layoutId: number) =>
  (await apiClient.get(`/api/mif/layouts/${layoutId}`)).data as {
    layout: MifLayout & { identity_column: string | null; period_column: string | null };
    columns: DerivedColumn[];
  };

/**
 * Read a plan's file and propose a layout from it. The result is always a DRAFT
 * with every column protected — a file cannot say which columns the plan echoes
 * back from your own reporting, so ownership stays a human decision.
 */
export const deriveLayout = async (
  file: File,
  params: { plan_key: string; plan_name: string; layout_version: string; source_note?: string }
) => {
  const form = new FormData();
  form.append('file', file);
  const response = await apiClient.post(`/api/mif/layouts/derive`, form, {
    params,
    headers: { 'Content-Type': undefined },
  });
  return response.data as {
    layout_id: number;
    created: boolean;
    origin: string;
    status: string;
    identity_column: string | null;
    summary: {
      columns: number;
      with_a_candidate_field: number;
      needing_a_field: number;
      high_confidence: number;
      transforms: Record<string, number>;
    };
    message: string;
  };
};

export const setLayoutStatus = async (layoutId: number, status: 'published' | 'retired' | 'draft') =>
  (await apiClient.post(`/api/mif/layouts/${layoutId}/status`, { status })).data as {
    layout_id: number;
    status: string;
  };

export const listPacks = async () =>
  (await apiClient.get(`/api/mif/packs`)).data as { packs: MifPack[] };

export const installPack = async (planKey: string, layoutId?: number) =>
  (await apiClient.post(`/api/mif/packs/install`, { plan_key: planKey, layout_id: layoutId })).data as {
    pack_id: number;
    status: string;
    column_count: number;
    policy_counts: Record<string, number>;
    unmapped_columns: string[];
    layout_id?: number;
    layout_origin?: string;
    message: string;
  };

export const getPackRules = async (packId: number) =>
  (await apiClient.get(`/api/mif/packs/${packId}/rules`)).data as {
    pack: { id: number; status: string; plan_name: string; layout_version: string };
    rules: MifFieldRule[];
    counts: Record<string, number>;
  };

export const updatePackRule = async (
  packId: number,
  mifColumn: string,
  changes: Partial<Pick<MifFieldRule, 'target_source' | 'target_field' | 'data_owner' | 'write_policy' | 'transform' | 'rationale'>>
) =>
  (await apiClient.patch(`/api/mif/packs/${packId}/rules/${encodeURIComponent(mifColumn)}`, changes)).data as MifFieldRule;

export interface TargetFieldOption {
  source: 'core' | 'custom';
  field: string;
  label: string;
  type: string;
  str_len: number | null;
  group: string;
  /** The MIF column whose rule already writes to this field, if any. */
  claimed_by: string | null;
}

export interface TargetFieldOptions {
  options: TargetFieldOption[];
  write_policies: { value: WritePolicy; label: string; help: string }[];
  transforms: string[];
}

export const getTargetFields = async (packId: number) =>
  (await apiClient.get(`/api/mif/packs/${packId}/target-fields`)).data as TargetFieldOptions;

/**
 * Reopen an approved pack for editing. Succeeds only while no load has run under
 * it — see the backend for why that line is drawn there.
 */
export const revisePack = async (packId: number) =>
  (await apiClient.post(`/api/mif/packs/${packId}/revise`, {})).data as {
    pack_id: number;
    status: string;
    changed: boolean;
    message?: string;
  };

/** How these rules reached their current state: installs, edits, approvals, reopens. */
export interface AgentProposal {
  id: number;
  pack_id: number;
  mif_column: string;
  attribute: string;
  old_value: string | null;
  new_value: string | null;
  /** The agent's reasoning. Required by the backend — a proposal without one is refused. */
  note: string | null;
  actor_agent: string | null;
  created_at: string;
  target_field: string | null;
  current_policy: WritePolicy | null;
  plan_key: string | null;
  pack_status: string | null;
}

export const listProposals = async (packId?: number) =>
  (await apiClient.get(`/api/mif/proposals`, { params: packId ? { pack_id: packId } : {} })).data as {
    proposals: AgentProposal[];
  };

/**
 * Accept or reject one proposal. Accepting is what applies it — the agent's row
 * changed nothing on its own.
 */
export const decideProposal = async (changeId: number, accept: boolean, note?: string) =>
  (await apiClient.post(`/api/mif/proposals/${changeId}/decide`, { accept, note })).data as {
    change_id: number;
    status: string;
    message: string;
  };

// --- cross-organization reuse (steps 5-6) -----------------------------------
// Platform staff only: these read one organization's configuration into another.
// The backend enforces it; the UI hides the controls to avoid offering a button
// that would be refused.

export interface ClonePreview {
  source_pack: { id: number; cbo_id: number; plan_key: string; plan_name: string; layout_version: string };
  destination_cbo_id: number;
  destination_name: string | null;
  columns_total: number;
  would_resolve: number;
  /** The number that decides whether cloning is worth doing yet. */
  would_be_unmapped: number;
  missing_fields: { mif_column: string; field: string; was: string | null; write_policy: WritePolicy }[];
  destination_has_packs: { id: number; status: string; layout_version: string }[];
  message: string;
}

export interface FieldGap {
  missing: {
    name: string;
    friendly_name: string;
    type: string;
    str_len: number;
    declared_str_len: number;
    observed_longest: number | null;
    category: string | null;
    /** Set when the source's own data already overflows its declared width. */
    width_note: string | null;
  }[];
  already_present: string[];
  core: string[];
  message: string;
}

export const clonePreview = async (packId: number, destinationCboId: number) =>
  (await apiClient.get(`/api/mif/packs/${packId}/clone-preview`, {
    params: { destination_cbo_id: destinationCboId },
  })).data as ClonePreview;

export const clonePack = async (packId: number, destinationCboId: number, versionSuffix = '-cloned') =>
  (await apiClient.post(`/api/mif/packs/${packId}/clone`, {
    destination_cbo_id: destinationCboId,
    version_suffix: versionSuffix,
  })).data as {
    pack_id: number;
    plan_key: string;
    layout_version: string;
    status: string;
    columns: number;
    resolved: number;
    unmapped: number;
    message: string;
  };

/** Preview by default; `apply` creates the fields. */
export const provisionFields = async (
  destinationCboId: number,
  sourceCboId: number,
  fieldNames: string[],
  apply = false
) =>
  (await apiClient.post(`/api/mif/orgs/${destinationCboId}/provision-fields`, {
    source_cbo_id: sourceCboId,
    field_names: fieldNames,
    apply,
  })).data as FieldGap & { created?: string[]; count?: number; widened?: string[] };

export const getPackHistory = async (packId: number) =>
  (await apiClient.get(`/api/mif/packs/${packId}/history`)).data as { history: RuleChange[] };

/** Plan-side layout changes, accumulated across files rather than per upload. */
export const getDrift = async (planKey?: string) =>
  (await apiClient.get(`/api/mif/drift`, { params: planKey ? { plan_key: planKey } : {} })).data as {
    drift: LayoutDrift[];
    summary: DriftSummary;
  };

export const decideDrift = async (
  driftId: number,
  body: { status: 'acknowledged' | 'resolved' | 'dismissed' | 'open'; pack_id?: number; note?: string }
) => (await apiClient.post(`/api/mif/drift/${driftId}`, body)).data;

export const approvePack = async (packId: number) =>
  (await apiClient.post(`/api/mif/packs/${packId}/approve`, {})).data as {
    pack_id: number;
    status: string;
  };

// ---------------------------------------------------------------------------
// The load
// ---------------------------------------------------------------------------

export const uploadMif = async (file: File, planKey: string) => {
  const form = new FormData();
  form.append('file', file);
  // Content-Type must be unset, not set: the browser has to add the multipart
  // boundary itself. apiClient defaults to application/json, so overriding it to
  // undefined here is what lets axios fill it in.
  const response = await apiClient.post(`/api/mif/batches`, form, {
    params: { plan_key: planKey },
    headers: { 'Content-Type': undefined },
  });
  return response.data as UploadResult;
};

export const acknowledgeMissingColumns = async (batchId: number) =>
  (await apiClient.post(`/api/mif/batches/${batchId}/acknowledge-missing`, {})).data as {
    batch_id: number;
    status: string;
    acknowledged: boolean;
    acknowledged_at?: string;
  };

export const discardBatch = async (batchId: number) =>
  (await apiClient.post(`/api/mif/batches/${batchId}/discard`, {})).data as { batch_id: number; status: string };

export const planBatch = async (batchId: number) =>
  (await apiClient.post(`/api/mif/batches/${batchId}/plan`, {})).data as {
    batch_id: number;
    summary: PlanSummary;
    churn_preview: { member_id: number; member_cin: string; name: string; reason: string }[];
    ready_to_commit: boolean;
    message: string;
  };

export const listRows = async (
  batchId: number,
  params: { classification?: RowClassification; action?: CellAction; limit?: number; offset?: number } = {}
) =>
  (await apiClient.get(`/api/mif/batches/${batchId}/rows`, { params })).data as {
    total: number;
    rows: MifRow[];
  };

export const listGaps = async (batchId: number, includeResolved = false) =>
  (await apiClient.get(`/api/mif/batches/${batchId}/gaps`, {
    params: { include_resolved: includeResolved },
  })).data as { gaps: MifGap[]; count: number };

export const resolveGap = async (
  batchId: number,
  rowNumber: number,
  body: { resolution: 'link_existing' | 'create_new' | 'skip'; member_id?: number; note?: string }
) =>
  (await apiClient.post(`/api/mif/batches/${batchId}/rows/${rowNumber}/resolve`, body)).data;

export const searchMembers = async (batchId: number, query: string) =>
  (await apiClient.get(`/api/mif/batches/${batchId}/member-search`, {
    params: { q: query },
  })).data as { members: { id: number; first_name: string; last_name: string; dob: string; cin: string | null }[] };

export const commitBatch = async (batchId: number, force = false) =>
  (await apiClient.post(`/api/mif/batches/${batchId}/commit`, { confirm: true, force })).data as {
    committed: boolean;
    members_created: number;
    members_updated: number;
    cells_written: number;
    cells_skipped: number;
    cells_protected: number;
    churn_candidates: number;
    plan_summary: PlanSummary;
    message: string;
  };

// ---------------------------------------------------------------------------
// History, churn, rollback
// ---------------------------------------------------------------------------

export const listBatches = async (limit = 50, offset = 0) =>
  (await apiClient.get(`/api/mif/batches`, { params: { limit, offset } })).data as {
    total: number;
    batches: MifBatch[];
  };

export const getBatch = async (batchId: number) =>
  (await apiClient.get(`/api/mif/batches/${batchId}`)).data as { batch: MifBatch };

export const listChurn = async (batchId: number) =>
  (await apiClient.get(`/api/mif/batches/${batchId}/churn`)).data as {
    churn: ChurnItem[];
    count: number;
  };

export const reviewChurn = async (
  batchId: number,
  churnId: number,
  outcome: 'still_enrolled' | 'disenrolled' | 'unknown'
) =>
  (await apiClient.post(`/api/mif/batches/${batchId}/churn/${churnId}/review`, { outcome })).data;

export const previewRollback = async (batchId: number) =>
  (await apiClient.get(`/api/mif/batches/${batchId}/rollback-preview`)).data as RollbackPreview;

export const rollbackBatch = async (
  batchId: number,
  body: { include_conflicts?: boolean; note?: string } = {}
) =>
  (await apiClient.post(`/api/mif/batches/${batchId}/rollback`, { confirm: true, ...body })).data as {
    rolled_back: boolean;
    cells_reverted: number;
    members_deleted: number;
    left_alone: number;
    message: string;
  };

export const abandonBatch = async (batchId: number) =>
  (await apiClient.delete(`/api/mif/batches/${batchId}`)).data;

// ---------------------------------------------------------------------------
// Presentation helpers — kept here so the wizard and the history view describe a
// policy the same way.
// ---------------------------------------------------------------------------

export const POLICY_LABELS: Record<WritePolicy, string> = {
  plan_authoritative: 'Plan is the source of truth',
  new_only: 'Protected — new members only',
  fill_if_empty: 'Fill only if blank',
  ignore: 'Ignored',
  unmapped: 'Needs a field',
};

export const ACTION_LABELS: Record<CellAction, string> = {
  write: 'Will update',
  skip_org_owned: 'Protected — you own this field',
  skip_unchanged: 'No change',
  skip_frozen: 'Protected — field is frozen',
  skip_human_edited: 'Protected — edited since the file was made',
  skip_empty_source: 'File has no value',
  skip_fill_occupied: 'Already has a value',
  skip_ignored: 'Column ignored',
  skip_unmapped: 'Column has no field yet',
  error: 'Could not read the value',
};
